import React, { useState, useCallback, useMemo } from 'react';
import { Stack, Alert } from '@mui/material';
import { getErrorMessage } from '@billda/shared-ui';
import { LabourItemGroupDetails } from '../api/labourItemGroupsApi';
import { useLabourGroupsPanelStore } from '../stores/labourGroupsPanelStore';
import { useUnifiedGroupMutation } from '../hooks/useUnifiedGroupMutation';
import { useAvailableLabourItems } from '../hooks/useAvailableLabourItems';
import { GroupNameCard } from './panel/molecules/GroupNameCard';
import { LabourItemsAssignmentCard } from './panel/molecules/LabourItemsAssignmentCard';
import { GroupFormActions } from './panel/molecules/GroupFormActions';

interface GroupEditFormProps {
  group: LabourItemGroupDetails;
}

const GroupEditForm: React.FC<GroupEditFormProps> = ({ group }) => {
  const editedGroupName = useLabourGroupsPanelStore(
    state => state.editedGroupName
  );
  const setEditedGroupName = useLabourGroupsPanelStore(
    state => state.setEditedGroupName
  );
  const transferListSearch = useLabourGroupsPanelStore(
    state => state.transferListSearch
  );
  const setTransferListSearch = useLabourGroupsPanelStore(
    state => state.setTransferListSearch
  );
  const closePanel = useLabourGroupsPanelStore(state => state.closePanel);
  const resetState = useLabourGroupsPanelStore(state => state.resetState);
  const switchMode = useLabourGroupsPanelStore(state => state.switchMode);

  const [assignedItems, setAssignedItems] = useState(group.labourItems);

  const { data: allAvailableItems = [], isLoading: itemsLoading } =
    useAvailableLabourItems();

  const availableItems = useMemo(() => {
    const assignedIds = new Set(assignedItems.map(item => item.id));
    return allAvailableItems.filter(item => !assignedIds.has(item.id));
  }, [allAvailableItems, assignedItems]);

  const {
    mutate: saveGroup,
    isPending,
    error: mutationError,
  } = useUnifiedGroupMutation();

  const handleSave = useCallback(async () => {
    // Check if any changes were made
    const nameChanged = editedGroupName.trim() !== group.name;
    const currentItemIds = new Set(group.labourItems.map(item => item.id));
    const newItemIds = new Set(assignedItems.map(item => item.id));
    const itemsChanged =
      currentItemIds.size !== newItemIds.size ||
      Array.from(currentItemIds).some(id => !newItemIds.has(id));

    if (!nameChanged && !itemsChanged) {
      // No changes made, just switch to readonly
      switchMode('readonly');
      return;
    }

    // Save all changes in single API call
    saveGroup(
      {
        groupId: group.id,
        name: editedGroupName.trim(),
        labourItemDefinitionIds: assignedItems.map(item => item.id),
      },
      {
        onSuccess: () => {
          // Switch back to readonly mode after successful save
          switchMode('readonly');
        },
      }
    );
  }, [editedGroupName, group, assignedItems, saveGroup, switchMode]);

  const handleCancel = () => {
    setEditedGroupName(group.name);
    setAssignedItems(group.labourItems);
    setTransferListSearch('');
    closePanel();
    resetState();
  };

  const handleMoveToAssigned = useCallback(
    (itemId: string) => {
      const item = availableItems.find(item => item.id === itemId);
      if (item) {
        setAssignedItems(prev => [...prev, item]);
      }
    },
    [availableItems]
  );

  const handleMoveToAvailable = useCallback((itemId: string) => {
    setAssignedItems(prev => prev.filter(item => item.id !== itemId));
  }, []);

  const isFormValid = editedGroupName.trim().length > 0;

  return (
    <Stack spacing={3}>
      {mutationError && (
        <Alert severity="error">{getErrorMessage(mutationError)}</Alert>
      )}

      {/* Group Name Card */}
      <GroupNameCard
        name={editedGroupName}
        onChange={setEditedGroupName}
        disabled={isPending}
        dataTestId="group-name-input"
      />

      {/* Labour Items Assignment Card */}
      <LabourItemsAssignmentCard
        availableItems={availableItems}
        assignedItems={assignedItems}
        searchText={transferListSearch}
        onSearchChange={setTransferListSearch}
        onMoveToAssigned={handleMoveToAssigned}
        onMoveToAvailable={handleMoveToAvailable}
        loading={itemsLoading || isPending}
      />

      {/* Actions */}
      <GroupFormActions
        onCancel={handleCancel}
        onSubmit={handleSave}
        submitLabel="Save Changes"
        disabled={!isFormValid}
        loading={isPending}
        cancelTestId="cancel-edit-button"
        submitTestId="save-group-button"
      />
    </Stack>
  );
};

export default GroupEditForm;
